import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { CancellationReasonPassengerBookingEnum } from '@mopla/data-models';

import { MplButton } from '../../../atoms/MplButton/MplButton';
import { MplRadioButton } from '../../../atoms/MplRadioButton/MplRadioButton';
import { MplTextArea } from '../../../atoms/MplTextArea/MplTextArea';
import { MainWithFooterTemplate } from '../../../molecules/MainWithFooterTemplate/MainWithFooterTemplate';
import {
	EMlpIconTypes,
	EMplVariantTypes,
	MplIconMessage,
} from '../../../molecules/MplIconMessage/MplIconMessage';

import { FooterStyled } from './CancellationReasonFormV2.styled';
import { ICancellationReasonFormState } from './types';
import {
	MAX_OTHER_REASON_INPUT,
	useCancellationReasonForm,
} from './useCancellationReasonForm';

const cancellationReasonsListBase: CancellationReasonPassengerBookingEnum[] = [
	CancellationReasonPassengerBookingEnum.RECEIVING_CHEAPER,
	CancellationReasonPassengerBookingEnum.NOT_NEEDED_ANYMORE,
	CancellationReasonPassengerBookingEnum.OTHER_REASON,
];

interface ICancellationReasonFormV2Props {
	withDelayReason?: boolean;
	hasCancellationFee: boolean;
	onSubmit: (values: ICancellationReasonFormState) => Promise<void>;
	isCancelling: boolean;
}

export const CancellationReasonFormV2 = ({
	withDelayReason,
	hasCancellationFee,
	onSubmit,
	isCancelling,
}: ICancellationReasonFormV2Props) => {
	const { t } = useTranslation('booking');
	const {
		control,
		handleSubmit,
		watch,
		formState: { isValid },
	} = useCancellationReasonForm();

	const selectedReason = watch('reason');
	const isOtherReasonSelected =
		selectedReason === CancellationReasonPassengerBookingEnum.OTHER_REASON;

	const cancellationReasonsList = useMemo(
		() => [
			...(withDelayReason
				? [CancellationReasonPassengerBookingEnum.DELAY]
				: []),
			...cancellationReasonsListBase,
		],
		[withDelayReason]
	);

	return (
		<form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
			<MainWithFooterTemplate
				content={
					<Stack p={2} gap={2}>
						<Typography variant="h4-sb">
							{t('booked.cancel.reason.title')}
						</Typography>
						<Stack gap={4} mb={2}>
							<Controller
								name="reason"
								control={control}
								render={({ field }) => (
									<Stack gap={2}>
										{cancellationReasonsList.map((reason) => (
											<MplRadioButton
												key={reason}
												text={t(`booked.cancel.reason.options.${reason}`)}
												checked={field.value === reason}
												onChange={() => field.onChange(reason)}
												multiline
												dataTestId={`cancellationReasonFormV2-${reason}-radioButton`}
											/>
										))}

										{isOtherReasonSelected && (
											<Controller
												name="otherReasonText"
												control={control}
												render={({ field }) => (
													<MplTextArea
														minRows={3}
														maxRows={6}
														label={t('booked.cancel.reason.other_reason_label')}
														inputProps={{
															maxLength: MAX_OTHER_REASON_INPUT,
															'data-testid':
																'cancellationReasonFormV2-otherReason-textArea',
														}}
														helperText={`${
															field.value?.length || 0
														}/${MAX_OTHER_REASON_INPUT}`}
														data-testid="cancellationReasonFormV2-otherReason-input"
														{...field}
													/>
												)}
											/>
										)}
									</Stack>
								)}
							/>
						</Stack>
						{!hasCancellationFee && (
							<MplIconMessage
								type={EMlpIconTypes.Info}
								variant={EMplVariantTypes.Default}
								message={t('booked.cancel.reason.body_free')}
								textVariant="l1"
								dataTestid="cancellationReasonFormV2-freeCancellation-message"
							/>
						)}
					</Stack>
				}
				footer={
					<FooterStyled testId="cancellationReasonFormV2-footer">
						<MplButton
							type="submit"
							fullWidth
							disabled={!isValid || isCancelling}
							testId="cancellationReasonFormV2-confirm-button"
							showLoader={isCancelling}
						>
							{t('common:button.continue')}
						</MplButton>
					</FooterStyled>
				}
			/>
		</form>
	);
};
